import { AjaxBasics } from '@xt/client/helpers'
import { BindAll } from 'lodash-decorators'
import { action, observable } from 'mobx'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiHomework } from '@xt/client/api'
import { EnumVisibleRange } from '@xt/client/enums'
import type {
  UserContent,
  UserDairyResult,
  UserHomeworkResult,
  DairyUserContent,
  HomeworkUserContent,
  PageMeta,
  HomeworkTabs
} from './types.d'

export enum EnumLoadResult {
  /**
   * 异常取消
   */
  Cancel,
  /**
   * 接口调用失败
   */
  Error,
  /**
   * 数据加载成功
   */
  Loaded,
  /**
   * 没有下一页数据了
   */
  Complete
}

@BindAll()
export class ControllerUserContent {
  constructor($ajax: AjaxBasics) {}

  @observable dairyList: Array<UserContent> = []

  @observable dairyPageMeta: PageMeta = {
    inited: false,
    isLoading: false,
    hasMore: true,
    page: 0,
    pageSize: 20,
    lastId: undefined
  }

  @observable homeworkList: Array<UserContent> = []

  @observable homeworkPageMeta: Record<HomeworkTabs, PageMeta & { cached: Array<UserContent> }> = {
    all: { inited: false, isLoading: false, hasMore: true, page: 0, pageSize: 20, lastId: undefined, cached: [] },
    reviewed: { inited: false, isLoading: false, hasMore: true, page: 0, pageSize: 20, lastId: undefined, cached: [] },
    unreviewed: { inited: false, isLoading: false, hasMore: true, page: 0, pageSize: 20, lastId: undefined, cached: [] }
  }

  homeworkId2MomentIdCache = new Map<number, string | null>()

  async loadUserDairy() {
    const { isLoading, page, hasMore, pageSize, lastId } = this.dairyPageMeta

    if (isLoading) return EnumLoadResult.Cancel

    if (!hasMore) return EnumLoadResult.Complete

    try {
      this.updateDairyPageMeta({ isLoading: true })

      const [error, list] = await $service<Array<UserDairyResult>>({
        method: 'GET',
        url: EnumApiHomework.MyMomentList,
        params: {
          page: page + 1,
          pageSize,
          sourceTypes: '1,2,3',
          lastId
        }
      })

      if (error) return EnumLoadResult.Error

      this.appendDairyList(
        list.map(i => {
          return {
            ...i,
            type: 'dairy',
            id: i.id,
            like: i.like === true,
            tag: i.sourceDescription || '',
            cover: Array.isArray(i.pictures) && i.pictures.length ? i.pictures[0].waterUrl : undefined,
            originWidth: Array.isArray(i.pictures) && i.pictures.length ? i.pictures[0].originWidth : 0,
            originHeight: Array.isArray(i.pictures) && i.pictures.length ? i.pictures[0].originHeight : 0,
            likeCount: typeof i.likeCount === 'number' ? i.likeCount : 0,
            isPublic: i.visibleRange === EnumVisibleRange.Public
          }
        })
      )

      this.updateDairyPageMeta({
        inited: true,
        page: page + 1,
        hasMore: list.length === pageSize,
        lastId: list.length ? list[list.length - 1].id : lastId
      })

      // 是否还有更多数据状态处理
      return list.length === pageSize ? EnumLoadResult.Loaded : EnumLoadResult.Complete
    } finally {
      this.updateDairyPageMeta({ isLoading: false })
    }
  }

  async loadUserHomework(type: HomeworkTabs) {
    const { isLoading, hasMore, page, pageSize, lastId } = this.homeworkPageMeta[type]

    if (isLoading) return EnumLoadResult.Cancel

    if (!hasMore) return EnumLoadResult.Complete

    try {
      this.updateHomeworkPageMetaByTab(type, { isLoading: true })

      const [error, list] = await $service<UserHomeworkResult[]>({
        method: 'GET',
        url: EnumApiHomework.MyHomeworkList,
        params: {
          page: page + 1,
          pageSize,
          typeKey: type === 'reviewed' ? 1 : type === 'unreviewed' ? 0 : undefined,
          lastId
        }
      })

      if (error) return EnumLoadResult.Error

      this.updateHomeworkPageMetaByTab(type, {
        inited: true,
        page: page + 1,
        hasMore: list.length === pageSize,
        lastId: list.length ? list[list.length - 1].id : lastId,
        cached: list.map(i => {
          return {
            ...i,
            title: i.homeworkTitle,
            type: 'homework',
            id: i.id,
            // 作业这个地方暂时不支持点赞
            like: false,
            likeCount: 0,
            tag: i.title,
            cover: Array.isArray(i.picList) && i.picList.length ? i.picList[0].waterUrl : undefined,
            originWidth: Array.isArray(i.picList) && i.picList.length ? i.picList[0].originalWidth : 0,
            originHeight: Array.isArray(i.picList) && i.picList.length ? i.picList[0].originalHeight : 0,
            isPublic: i.visibleRange === EnumVisibleRange.Public
          }
        })
      })
      this.refreshHomeworkList(type)

      // 是否还有更多数据状态处理
      return list.length === pageSize ? EnumLoadResult.Loaded : EnumLoadResult.Complete
    } finally {
      this.updateHomeworkPageMetaByTab(type, { isLoading: false })
    }
  }

  async switchHomeworkTab(tab: HomeworkTabs) {
    console.warn('切换tab', tab)
    if (!this.homeworkPageMeta[tab].inited) {
      this.resetHomwork()
    } else {
      // 取缓存的数据填充
      this.refreshHomeworkList(tab)
    }
  }

  async likeDairy(momentId: string) {
    this.onLikeDairy(momentId, true)

    const [, success] = await $service<boolean>({
      method: 'PUT',
      url: EnumApiHomework.MomentLike,
      data: { momentId },
      params: { likeType: 'true', momentId }
    })

    // rollback state
    if (!success) {
      this.onLikeDairy(momentId, false)
    }

    return success === true
  }

  async getMomentIdByHomeworkId(id: number) {
    if (this.homeworkId2MomentIdCache.has(id)) {
      return this.homeworkId2MomentIdCache.get(id) as string | null
    }

    const [, momentId] = await $service<string | null>({
      url: EnumApiHomework.GetMomentIdByHomeworkId,
      method: 'GET',
      data: { sourceType: 3 },
      params: { sourceType: 3, bizId: id }
    })

    this.homeworkId2MomentIdCache.set(id, momentId)

    return momentId
  }

  @action
  resetHomwork() {
    this.homeworkList = []
  }

  @action
  refreshHomeworkList(tab: HomeworkTabs) {
    this.homeworkList = this.homeworkPageMeta[tab].cached
  }

  @action
  appendDairyList(newList: DairyUserContent[]) {
    this.dairyList = [...this.dairyList, ...newList]
  }

  @action
  appendHomeworkListByTab(tab: HomeworkTabs, newList: HomeworkUserContent[]) {
    this.homeworkPageMeta[tab].cached = [...this.homeworkPageMeta[tab].cached, ...newList]
  }

  @action
  onLikeDairy(momentId: string, isLike: boolean) {
    this.dairyList = this.dairyList.map(i => {
      if (i.id === momentId) {
        i.like = isLike
        i.likeCount = isLike ? i.likeCount + 1 : Math.max(0, i.likeCount - 1)
      }

      return i
    })
  }

  @action
  updateDairyPageMeta(options: Partial<PageMeta>) {
    this.dairyPageMeta = {
      ...this.dairyPageMeta,
      ...options
    }
  }

  @action
  updateHomeworkPageMetaByTab(tab: HomeworkTabs, options: Partial<PageMeta & { cached: Array<UserContent> }>) {
    const { cached = [], ...rest } = options

    this.homeworkPageMeta[tab] = {
      ...this.homeworkPageMeta[tab],
      ...rest,
      cached: [...this.homeworkPageMeta[tab].cached, ...cached]
    }
  }
}
